import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Loader2, CheckCircle2, Upload, X, ShieldCheck, ArrowRight, ArrowLeft,
  User, MapPin, FileSignature, Camera, BadgeCheck, Hash, Heart, Stethoscope,
  Shield, Activity, HeartPulse, Eye, EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { publicAPI } from '@/lib/publicApi';
import { US_STATES, PRODUCT_LINES, formatPhoneInput } from '@/lib/utils';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';

const NAVY = '#0b1e39';
const GOLD = '#d4a244';

const STEPS = [
  { id: 'identity', title: 'Your Details', icon: User },
  { id: 'sensitive', title: 'Secure Info', icon: MapPin },
  { id: 'contracts', title: 'Contracts', icon: FileSignature },
  { id: 'profile', title: 'Profile', icon: Camera },
  { id: 'review', title: 'Review & Submit', icon: BadgeCheck },
];

const CATEGORY_ICONS = {
  Medicare: Stethoscope,
  ACA: HeartPulse,
  Life_Annuity: Heart,
  Ancillary: Shield,
  Private_Health_Plans: Activity,
};

/* ---------- shells (module scope so inputs keep focus across re-renders) ---------- */
const Page = ({ children }) => (
  <div className="min-h-screen w-full relative flex items-center justify-center p-4 sm:p-6 overflow-hidden" style={{ background: '#f6f7fa' }}>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage: 'url(/brand/kr-logo.webp)',
        backgroundRepeat: 'repeat',
        backgroundSize: '300px auto',
        backgroundPosition: 'center top',
        opacity: 0.13,
      }}
    />
    <div className="w-full max-w-2xl relative z-10">{children}</div>
  </div>
);

const Header = ({ compact }) => (
  <div
    className="flex items-center justify-between px-6 sm:px-9 py-6 rounded-t-2xl"
    style={{ background: `linear-gradient(135deg, ${NAVY}, #14315c)`, borderBottom: `3px solid ${GOLD}` }}
  >
    <div>
      <div className="text-[11px] font-bold tracking-[0.2em] uppercase" style={{ color: GOLD }}>KR Enterprise Group</div>
      <div className="text-white text-lg font-semibold mt-0.5">Agent Application</div>
    </div>
    {!compact && (
      <div className="hidden sm:flex items-center gap-1.5 rounded-full px-3 py-1" style={{ background: 'rgba(212,162,68,0.14)', border: `1px solid ${GOLD}55` }}>
        <ShieldCheck size={13} style={{ color: GOLD }} />
        <span className="text-[11px] font-semibold" style={{ color: GOLD }}>256-bit Secure</span>
      </div>
    )}
  </div>
);

const Card = ({ children }) => (
  <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 24px 60px rgba(11,30,57,0.10)' }}>
    {children}
  </div>
);

export default function AgentApplicationPage() {
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showSsn, setShowSsn] = useState(false);
  const [statusToken, setStatusToken] = useState(null);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', npn: '',
    ssn_full: '', date_of_birth: '',
    address_line1: '', address_line2: '', city: '', state: '', zip: '',
    preferred_language: 'en', photo_base64: null, categories: [],
  });
  const setField = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => {
      if (!e[k]) return e;
      const n = { ...e }; delete n[k]; return n;
    });
  };
  const toggleCategory = (value) => {
    setForm((p) => ({
      ...p,
      categories: p.categories.includes(value)
        ? p.categories.filter((c) => c !== value)
        : [...p.categories, value],
    }));
    setErrors((e) => { if (!e.categories) return e; const n = { ...e }; delete n.categories; return n; });
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be 10 MB or less'); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm((p) => ({ ...p, photo_base64: reader.result })); setPhotoPreview(reader.result); };
    reader.readAsDataURL(file);
  };
  const clearPhoto = () => {
    setForm((p) => ({ ...p, photo_base64: null }));
    setPhotoPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const fieldErrors = (s) => {
    const e = {};
    if (s === 0) {
      if (!form.first_name.trim()) e.first_name = 'First name is required';
      if (!form.last_name.trim()) e.last_name = 'Last name is required';
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = 'Enter a valid email address';
      if (!form.phone.replace(/\D/g, '')) e.phone = 'Phone number is required';
      if (!form.npn.replace(/\D/g, '')) e.npn = 'NPN is required';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date_of_birth)) e.date_of_birth = 'Date of birth is required';
    }
    if (s === 1) {
      if (form.ssn_full.replace(/\D/g, '').length !== 9) e.ssn_full = 'Enter a valid 9-digit SSN';
      if (!form.address_line1.trim()) e.address_line1 = 'Street address is required';
      if (!form.city.trim()) e.city = 'City is required';
      if (!form.state) e.state = 'State is required';
      if (!form.zip.trim()) e.zip = 'ZIP is required';
    }
    if (s === 2) {
      if (form.categories.length === 0) e.categories = 'Select at least one category';
    }
    return e;
  };

  const next = () => {
    const e = fieldErrors(step);
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setDir(1); setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => { setDir(-1); setStep((s) => Math.max(s - 1, 0)); };

  const errCls = (f) => (errors[f] ? ' border-red-400 focus-visible:ring-red-400' : '');
  const FieldError = ({ f }) => (errors[f]
    ? <p className="mt-1 text-xs font-medium text-red-500" data-testid={`app-error-${f}`}>{errors[f]}</p>
    : null);

  const celebrate = () => {
    const end = Date.now() + 900;
    const colors = [GOLD, '#ffffff', '#e5b95d'];
    (function frame() {
      confetti({ particleCount: 3, angle: 60, spread: 60, origin: { x: 0 }, colors });
      confetti({ particleCount: 3, angle: 120, spread: 60, origin: { x: 1 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
  };

  const handleSubmit = async () => {
    for (let s = 0; s <= 2; s += 1) {
      const e = fieldErrors(s);
      if (Object.keys(e).length) { setErrors(e); setDir(-1); setStep(s); return; }
    }
    setErrors({});
    setSubmitting(true);
    try {
      const { data } = await publicAPI.submitAgentApplication({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        npn: form.npn.trim(),
        ssn_full: form.ssn_full,
        date_of_birth: form.date_of_birth,
        address_line1: form.address_line1.trim(),
        address_line2: form.address_line2.trim() || null,
        city: form.city.trim(),
        state: form.state,
        zip: form.zip.trim(),
        preferred_language: form.preferred_language,
        categories: form.categories,
        photo_base64: form.photo_base64,
      });
      setStatusToken(data?.status_token || null);
      setDone(true);
      toast.dismiss();
      celebrate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Page><Card><Header compact />
        <div className="text-center px-6 py-14" data-testid="application-success">
          <motion.div
            initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 12 }}
            className="mx-auto mb-5 h-20 w-20 rounded-full flex items-center justify-center relative"
            style={{ background: NAVY }}
          >
            <span className="absolute inset-0 rounded-full animate-ping" style={{ background: `${GOLD}40` }} />
            <CheckCircle2 className="relative" size={40} style={{ color: GOLD }} />
          </motion.div>
          <h2 className="text-2xl font-semibold" style={{ color: NAVY }}>Application received, {form.first_name}!</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
            Thank you for applying to join KR Enterprise Group. Our onboarding team is reviewing your
            details. Once approved, you'll receive a secure email with your portal access to begin contracting.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2" style={{ background: '#f4f7fb', border: '1px solid #e5e9f0' }}>
            <ShieldCheck size={15} style={{ color: '#15794a' }} />
            <span className="text-xs font-medium text-slate-600">A confirmation email is on its way</span>
          </div>
          {statusToken && (
            <div className="mt-7 max-w-md mx-auto">
              <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 text-left">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Track your application</div>
                <p className="text-xs text-slate-500 mb-3">Bookmark your private link to check your status anytime — no login needed.</p>
                <a
                  href={`/application-status/${statusToken}`}
                  data-testid="application-status-link"
                  className="inline-flex items-center justify-center gap-2 w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ background: NAVY }}
                >
                  Check application status
                  <ArrowRight size={15} style={{ color: GOLD }} />
                </a>
              </div>
            </div>
          )}
        </div>
      </Card>
      <p className="text-center text-[11px] text-slate-400 mt-4">© KR Enterprise Group · Confidential &amp; secure onboarding</p>
      </Page>
    );
  }

  const CurrentIcon = STEPS[step].icon;

  return (
    <Page>
      <Card>
        <Header />

        {/* Stepper */}
        <div className="px-6 sm:px-9 pt-7" data-testid="application-stepper">
          <div className="flex items-center">
            {STEPS.map((st, i) => {
              const StIcon = st.icon;
              const active = i === step;
              const complete = i < step;
              return (
                <React.Fragment key={st.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className="h-9 w-9 rounded-full flex items-center justify-center transition-all"
                      style={{
                        background: complete ? '#15794a' : active ? NAVY : '#eef1f6',
                        boxShadow: active ? `0 0 0 4px ${GOLD}33` : 'none',
                        color: complete || active ? '#fff' : '#9aa6b8',
                      }}
                    >
                      {complete ? <CheckCircle2 size={16} /> : <StIcon size={15} />}
                    </div>
                    <span className="hidden sm:block text-[10px] mt-1.5 font-medium" style={{ color: active ? NAVY : '#9aa6b8' }}>{st.title}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="flex-1 h-0.5 mx-1.5 rounded" style={{ background: i < step ? '#15794a' : '#e5e9f0' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step body */}
        <div className="px-6 sm:px-9 py-7 min-h-[340px]">
          <div className="flex items-center gap-2 mb-5">
            <CurrentIcon size={18} style={{ color: GOLD }} />
            <h3 className="text-lg font-semibold" style={{ color: NAVY }}>{STEPS[step].title}</h3>
          </div>

          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={step}
              custom={dir}
              initial={{ opacity: 0, x: dir * 28 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -28 }}
              transition={{ duration: 0.25 }}
              className="space-y-4"
            >
              {step === 0 && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="fn">First name (as on license) *</Label>
                      <Input id="fn" value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} className={errCls('first_name')} data-testid="app-first-name" />
                      <FieldError f="first_name" />
                    </div>
                    <div>
                      <Label htmlFor="ln">Last name (as on license) *</Label>
                      <Input id="ln" value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} className={errCls('last_name')} data-testid="app-last-name" />
                      <FieldError f="last_name" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="em">Email *</Label>
                    <Input id="em" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@email.com" className={errCls('email')} data-testid="app-email" />
                    <FieldError f="email" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="ph">Phone *</Label>
                      <Input id="ph" value={formatPhoneInput(form.phone)} onChange={(e) => setField('phone', e.target.value)} placeholder="(863) 555-1234" className={errCls('phone')} data-testid="app-phone" />
                      <FieldError f="phone" />
                    </div>
                    <div>
                      <Label htmlFor="npn">NPN *</Label>
                      <Input id="npn" value={form.npn} onChange={(e) => setField('npn', e.target.value.replace(/\D/g, ''))} inputMode="numeric" placeholder="National Producer Number" className={errCls('npn')} data-testid="app-npn" />
                      <FieldError f="npn" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="dob">Date of birth *</Label>
                    <Input id="dob" type="date" value={form.date_of_birth} onChange={(e) => setField('date_of_birth', e.target.value)} className={errCls('date_of_birth')} data-testid="app-dob" />
                    <FieldError f="date_of_birth" />
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <div className="flex items-center gap-2 rounded-lg px-3 py-2 mb-1" style={{ background: '#f6fbf7', border: '1px solid #cbe8d6' }}>
                    <ShieldCheck size={15} style={{ color: '#15794a' }} />
                    <span className="text-xs font-medium" style={{ color: '#15794a' }}>Your SSN is encrypted with 256-bit security and never shared.</span>
                  </div>
                  <div>
                    <Label htmlFor="ssn">Social Security Number *</Label>
                    <div className="relative">
                      <Input id="ssn" type={showSsn ? 'text' : 'password'} value={form.ssn_full} onChange={(e) => setField('ssn_full', e.target.value)} placeholder="XXX-XX-XXXX" inputMode="numeric" autoComplete="off" data-testid="app-ssn" className={`pr-10${errCls('ssn_full')}`} style={{ fontFamily: 'JetBrains Mono, monospace' }} />
                      <button type="button" onClick={() => setShowSsn((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={showSsn ? 'Hide SSN' : 'Show SSN'} data-testid="app-ssn-toggle">
                        {showSsn ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <FieldError f="ssn_full" />
                  </div>
                  <div>
                    <Label htmlFor="a1">Street address *</Label>
                    <AddressAutocomplete
                      value={form.address_line1}
                      onChange={(v) => setField('address_line1', v)}
                      onPlaceSelected={(p) => {
                        setForm((prev) => ({
                          ...prev,
                          address_line1: p.street || prev.address_line1,
                          city: p.city || prev.city,
                          state: p.state || prev.state,
                          zip: p.zip || prev.zip,
                        }));
                        setErrors((e) => {
                          const n = { ...e };
                          ['address_line1', 'city', 'state', 'zip'].forEach((k) => delete n[k]);
                          return n;
                        });
                      }}
                      placeholder="Start typing your address…"
                      testId="app-address"
                    />
                    <FieldError f="address_line1" />
                  </div>
                  <div>
                    <Label htmlFor="a2">Apt / Suite (optional)</Label>
                    <Input id="a2" value={form.address_line2} onChange={(e) => setField('address_line2', e.target.value)} data-testid="app-address2" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <Label htmlFor="city">City *</Label>
                      <Input id="city" value={form.city} onChange={(e) => setField('city', e.target.value)} className={errCls('city')} data-testid="app-city" />
                      <FieldError f="city" />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <Select value={form.state} onValueChange={(v) => setField('state', v)}>
                        <SelectTrigger className={errCls('state')} data-testid="app-state"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <FieldError f="state" />
                    </div>
                    <div>
                      <Label htmlFor="zip">ZIP *</Label>
                      <Input id="zip" value={form.zip} onChange={(e) => setField('zip', e.target.value)} className={errCls('zip')} data-testid="app-zip" />
                      <FieldError f="zip" />
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <p className="text-sm text-slate-500">Select the contract categories you'd like us to send you. Choose all that apply.</p>
                  <FieldError f="categories" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="app-categories">
                    {PRODUCT_LINES.map((cat) => {
                      const Icon = CATEGORY_ICONS[cat.value] || FileSignature;
                      const selected = form.categories.includes(cat.value);
                      return (
                        <button
                          type="button"
                          key={cat.value}
                          onClick={() => toggleCategory(cat.value)}
                          data-testid={`app-category-${cat.value}`}
                          className="flex items-center gap-3 rounded-xl px-4 py-3.5 text-left transition-all"
                          style={{
                            border: selected ? `2px solid ${NAVY}` : '2px solid #e5e9f0',
                            background: selected ? '#f4f7fb' : '#fff',
                            boxShadow: selected ? `0 0 0 4px ${GOLD}22` : 'none',
                          }}
                        >
                          <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: selected ? NAVY : '#eef1f6', color: selected ? GOLD : '#9aa6b8' }}>
                            <Icon size={17} />
                          </div>
                          <span className="flex-1 text-sm font-medium" style={{ color: selected ? NAVY : '#475569' }}>{cat.label}</span>
                          <div className="h-5 w-5 rounded-full flex items-center justify-center shrink-0" style={{ border: selected ? 'none' : '2px solid #cbd5e1', background: selected ? '#15794a' : 'transparent' }}>
                            {selected && <CheckCircle2 size={14} className="text-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div>
                    <Label>Profile photo (optional)</Label>
                    <div className="flex items-center gap-4 mt-1.5">
                      {photoPreview ? (
                        <div className="relative">
                          <img src={photoPreview} alt="preview" className="w-20 h-20 rounded-full object-cover border-2" style={{ borderColor: GOLD }} />
                          <button type="button" onClick={clearPhoto} className="absolute -top-1 -right-1 bg-slate-800 text-white rounded-full p-0.5" data-testid="app-photo-clear"><X size={12} /></button>
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-slate-100 border flex items-center justify-center text-slate-300"><Upload size={20} /></div>
                      )}
                      <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="text-sm" data-testid="app-photo-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Preferred language</Label>
                    <Select value={form.preferred_language} onValueChange={(v) => setField('preferred_language', v)}>
                      <SelectTrigger data-testid="app-language"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Español</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {step === 4 && (
                <div className="space-y-3" data-testid="app-review">
                  <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                    {[
                      ['Name', `${form.first_name} ${form.last_name}`],
                      ['Email', form.email],
                      ['Phone', formatPhoneInput(form.phone)],
                      ['NPN', form.npn],
                      ['Date of birth', form.date_of_birth],
                      ['SSN', `•••-••-${form.ssn_full.replace(/\D/g, '').slice(-4)}`],
                      ['Address', [form.address_line1, form.address_line2, `${form.city}, ${form.state} ${form.zip}`].filter(Boolean).join(', ')],
                      ['Contracts', form.categories.map((c) => (PRODUCT_LINES.find((p) => p.value === c) || {}).label || c).join(', ')],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4 px-4 py-2.5 text-sm">
                        <span className="text-slate-400">{k}</span>
                        <span className="font-medium text-slate-800 text-right truncate">{v}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400">By submitting, you confirm this information is accurate. A KR administrator will review your application before any portal access is granted.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer nav */}
        <div className="px-6 sm:px-9 py-5 border-t border-slate-100 flex items-center justify-between">
          <Button variant="ghost" onClick={back} disabled={step === 0 || submitting} data-testid="app-back" className="text-slate-500">
            <ArrowLeft size={15} className="mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={next} className="text-white" style={{ background: NAVY }} data-testid="app-next">
              Continue <ArrowRight size={15} className="ml-1.5" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="text-white" style={{ background: '#15794a' }} data-testid="app-submit">
              {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <BadgeCheck size={16} className="mr-1.5" />}
              Submit Application
            </Button>
          )}
        </div>
      </Card>
      <p className="text-center text-[11px] text-slate-400 mt-4">© KR Enterprise Group · Confidential &amp; secure onboarding</p>
    </Page>
  );
}
